import type { ReactElement, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import { getCompanyReport } from '../api/operations';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../lib/icons';
import {
  EMPLOYEE_BADGE,
  EMPLOYEE_COLOUR,
  TICKET_BADGE,
  TICKET_COLOUR,
  badgeFor,
} from '../lib/statusStyles';
import { dateLabel, timeLabel } from '../lib/calendar';

export interface CompanyReportPageProps {
  companyId: string;
  onBack: () => void;
  onEditBasics: () => void;
}

/**
 * FEAT-021 — the company report.
 *
 * Read-only by design: the single mutating control is "Edit basics", which opens
 * the FEAT-011 form and is hidden without `Companies.Manage` — and refused
 * server-side regardless (REQ-115).
 */
export function CompanyReportPage({
  companyId,
  onBack,
  onEditBasics,
}: CompanyReportPageProps): ReactElement {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();

  const query = useQuery({
    queryKey: ['company-report', companyId],
    queryFn: () => getCompanyReport(companyId),
  });

  if (query.isPending) {
    return (
      <div className="empty" role="status" aria-live="polite">
        {t('loading')}
      </div>
    );
  }

  if (query.error || !query.data) {
    return (
      <div className="empty" role="alert">
        {query.error instanceof ApiClientError ? query.error.message : t('login_network_error')}
      </div>
    );
  }

  const report = query.data;
  const failed = (band: string): boolean => report.unavailable.includes(band);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('companies')}</div>
          <h1>{report.company.name}</h1>
          <div className="lead">{t('rep_view_only')}</div>
        </div>
        <div className="row-actions">
          <button type="button" className="btn" onClick={onBack}>
            {t('rep_back')}
          </button>
          {/* UX only — the server refuses this without Companies.Manage. */}
          {can('Companies.Manage') ? (
            <button type="button" className="btn primary" onClick={onEditBasics}>
              <Icon name="edit" />
              {t('rep_edit_basics')}
            </button>
          ) : null}
        </div>
      </div>

      <div className="kpis">
        <Kpi label={t('kpi_employees')} value={report.kpis.employees} />
        <Kpi label={t('kpi_active')} value={report.kpis.active} />
        <Kpi label={t('kpi_inactive')} value={report.kpis.inactive} />
        <Kpi label={t('kpi_managers')} value={report.kpis.managers} />
        <Kpi label={t('kpi_contacts')} value={report.kpis.comm_contacts} />
        <Kpi label={t('kpi_meetings')} value={report.kpis.meetings} />
        <Kpi label={t('kpi_tickets')} value={report.kpis.tickets} />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t('rep_info')}</h2>
        </div>
        <div style={{ padding: '4px 18px 18px' }}>
          <dl className="dl">
            <dt>{t('col_cr')}</dt>
            <dd>{report.company.cr_number}</dd>

            <dt>{t('col_website')}</dt>
            <dd>{report.company.website ?? '—'}</dd>

            <dt>{t('co_created')}</dt>
            <dd>{dateLabel(report.company.created_on, i18n.language)}</dd>

            {/* REQ-114 — both lists derived from employee records. */}
            <dt>{t('rep_managers')}</dt>
            <dd>
              {report.company.managers.length === 0 ? (
                <span className="chip">{t('rep_no_data')}</span>
              ) : (
                report.company.managers.map((m) => (
                  <span className="chip" key={m.id}>
                    {m.name}
                  </span>
                ))
              )}
            </dd>

            <dt>{t('rep_contacts')}</dt>
            <dd>
              {report.company.comm_contacts.length === 0 ? (
                <span className="chip">{t('rep_no_data')}</span>
              ) : (
                report.company.comm_contacts.map((c) => (
                  <span className="chip" key={c.id}>
                    {c.name}
                  </span>
                ))
              )}
            </dd>
          </dl>
        </div>
      </div>

      {/*
        The prototype's own chart band: a donut for employees by status, bars for
        tickets. `.rep-grid`, `.donut` and `.rep-bars` are all vendored styles —
        this is its markup, not a reinterpretation of it.
      */}
      <div className="rep-grid">
        <div className="card">
          <div className="card-head">
            <h2>{t('rep_employees_by_status')}</h2>
          </div>
          <div style={{ padding: '16px 18px' }}>
            <Donut
              segments={report.employees_by_status.map((row) => ({
                label: t(`es_${row.status}`, row.status),
                value: row.count,
                color: EMPLOYEE_COLOUR[row.status] ?? 'var(--muted)',
              }))}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{t('rep_tickets_by_status')}</h2>
          </div>
          <div style={{ padding: '16px 18px' }}>
            <Bars
              items={report.tickets_by_status.map((row) => ({
                label: t(`st_${row.status}`, row.status),
                value: row.count,
                color: TICKET_COLOUR[row.status] ?? 'var(--muted)',
              }))}
            />
          </div>
        </div>
      </div>

      <Band
        title={t('employees')}
        failed={failed('employees')}
        empty={report.employees.length === 0}
      >
        <table>
          <thead>
            <tr>
              <th scope="col">{t('emp_name')}</th>
              <th scope="col">{t('emp_office')}</th>
              <th scope="col">{t('emp_role')}</th>
              <th scope="col">{t('status')}</th>
            </tr>
          </thead>
          <tbody>
            {report.employees.map((person) => (
              <tr key={person.id}>
                <td>
                  <div className="t-title">{person.name}</div>
                  <div className="t-sub">{person.email}</div>
                </td>
                <td>{person.office ?? '—'}</td>
                <td>
                  {t(`role_${person.role}`, person.role)}
                  {person.is_comm_contact ? (
                    <span className="chip">{t('rep_contacts')}</span>
                  ) : null}
                </td>
                <td>
                  <span className={badgeFor(EMPLOYEE_BADGE, person.status)}>
                    {t(`es_${person.status}`, person.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Band>

      <Band
        title={t('rep_meetings')}
        failed={failed('meetings')}
        empty={report.meetings.length === 0}
      >
        <table>
          <thead>
            <tr>
              <th scope="col">{t('bk_purpose')}</th>
              <th scope="col">{t('bk_room')}</th>
              <th scope="col">{t('bk_when')}</th>
              <th scope="col">{t('bk_organizer')}</th>
            </tr>
          </thead>
          <tbody>
            {report.meetings.map((meeting) => (
              <tr key={meeting.id}>
                <td>
                  <div className="t-title">{meeting.title}</div>
                </td>
                <td>{meeting.room_name}</td>
                <td className="num">
                  <bdi>{timeLabel(meeting.starts_at, i18n.language)}</bdi>
                </td>
                <td>{meeting.organizer}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Band>

      <Band title={t('complaints')} failed={failed('tickets')} empty={report.tickets.length === 0}>
        <table>
          <thead>
            <tr>
              <th scope="col">{t('col_id')}</th>
              <th scope="col">{t('col_subject')}</th>
              <th scope="col">{t('col_type')}</th>
              <th scope="col">{t('status')}</th>
            </tr>
          </thead>
          <tbody>
            {report.tickets.map((ticket) => (
              <tr key={ticket.id}>
                <td className="num">{ticket.id}</td>
                <td>{ticket.subject}</td>
                <td>{t(`tt_${ticket.type}`, ticket.type)}</td>
                <td>
                  <span className={badgeFor(TICKET_BADGE, ticket.status)}>
                    {t(`st_${ticket.status}`, ticket.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Band>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="kpi">
      <div className="kl">{label}</div>
      <div className="kv num">{value}</div>
    </div>
  );
}

/**
 * A band, with the three states the FRS distinguishes.
 *
 * "Failed to load" and "genuinely empty" are different statements and must read
 * differently: telling someone there is no data when the query simply broke is
 * the more damaging of the two mistakes.
 */
function Band({
  title,
  failed,
  empty,
  children,
}: {
  title: string;
  failed: boolean;
  empty: boolean;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="card">
      <div className="card-head">
        <h2>{title}</h2>
      </div>
      {failed ? (
        <div className="empty" role="alert">
          {t('rep_unavailable')}
        </div>
      ) : empty ? (
        <div className="empty">{t('rep_no_data')}</div>
      ) : (
        <div className="table-wrap">{children}</div>
      )}
    </div>
  );
}

/**
 * The prototype's donut, drawn with the same stroke-dasharray arithmetic.
 *
 * Two things are added, neither of which moves a pixel. The SVG carries
 * `role="img"` and a description naming every segment and its count, because an
 * arc conveys nothing to a screen reader; and the legend beneath states each
 * figure in text, which the prototype's own legend already did.
 */
function Donut({
  segments,
}: {
  segments: { label: string; value: number; color: string }[];
}): ReactElement {
  const { t } = useTranslation();

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) return <div className="rep-empty">{t('rep_no_data')}</div>;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  const arcs = segments
    .filter((s) => s.value > 0)
    .map((s) => {
      const length = (s.value / total) * circumference;
      const arc = (
        <circle
          key={s.label}
          r={radius}
          cx={70}
          cy={70}
          fill="none"
          stroke={s.color}
          strokeWidth={18}
          strokeDasharray={`${length.toFixed(2)} ${(circumference - length).toFixed(2)}`}
          strokeDashoffset={(-offset).toFixed(2)}
          transform="rotate(-90 70 70)"
        />
      );
      offset += length;
      return arc;
    });

  const description = segments.map((s) => `${s.label}: ${s.value}`).join(', ');

  return (
    <>
      <svg
        viewBox="0 0 140 140"
        className="donut"
        role="img"
        aria-label={`${description} — ${total}`}
      >
        {arcs}
        <text x={70} y={79} textAnchor="middle" className="donut-num">
          {total}
        </text>
      </svg>

      <div className="rep-legend">
        {segments.map((s) => (
          <div className="rep-lg" key={s.label}>
            <span className="dot" style={{ background: s.color }} />
            {s.label} <b>{s.value}</b>
          </div>
        ))}
      </div>
    </>
  );
}

/** The prototype's horizontal bars, scaled to the largest value. */
function Bars({
  items,
}: {
  items: { label: string; value: number; color: string }[];
}): ReactElement {
  const { t } = useTranslation();

  if (!items.some((i) => i.value > 0)) return <div className="rep-empty">{t('rep_no_data')}</div>;

  const max = Math.max(1, ...items.map((i) => i.value));

  return (
    <div className="rep-bars">
      {items.map((item) => (
        <div className="rep-bar" key={item.label}>
          <div className="rep-bl">{item.label}</div>
          {/* The track is decoration; the figure beside it is the content. */}
          <div className="rep-track" aria-hidden="true">
            <div
              className="rep-fill"
              style={{ width: `${Math.round((item.value / max) * 100)}%`, background: item.color }}
            />
          </div>
          <div className="rep-bv num">{item.value}</div>
        </div>
      ))}
    </div>
  );
}
